import { Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { API_BASE_URL } from "@/config/api";
import { SuccessResponse, User } from "@/types/api";
import { useToast } from "@/components/ui/use-toast";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { toast } = useToast();
  const token = localStorage.getItem("token");

  const { data, isLoading } = useQuery({
    queryKey: ["user"],
    queryFn: async () => {
      if (!token) {
        throw new Error("No token found");
      }

      const response = await fetch(`${API_BASE_URL}/user/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Authentication failed");
      }

      const data = await response.json();
      return data as SuccessResponse<User>;
    },
    meta: {
      onError: (error: Error) => {
        localStorage.removeItem("token");
        localStorage.removeItem("isAdmin");
        toast({
          title: "Authentication Error",
          description: error.message || "Please login again",
          variant: "destructive",
        });
      },
    },
    retry: false,
    enabled: !!token, // Only run query if token exists
  });

  console.log("Protected Route State:", {
    isLoading,
    hasData: !!data?.data,
    token: !!token,
  });

  // If there's no token, redirect to login immediately
  if (!token) {
    console.log("No token found, redirecting to login");
    return <Navigate to="/login" replace />;
  }

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        Loading...
      </div>
    );
  }

  // Add check for inactive user
  if (data?.data && !data.data.active) {
    return <Navigate to="/inactive" replace />;
  }

  // If we have the user data, render the protected content
  if (data) {
    return children;
  }

  // If we get here, we have a token but no valid user data
  console.log("No user data found, redirecting to login");
  console.debug("Data:", data);
  return <Navigate to="/login" replace />;
};

export default ProtectedRoute;
